import React, { useEffect, useRef } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useT } from "@/hooks/useT";

/**
 * Full-screen loading overlay played when the user explicitly toggles the
 * language. Duration ~1.8s; dismissible via Skip / Esc / click / Enter /
 * Space. Renders a low-poly icosahedron mark in violet, a bilingual
 * quote block, and a Skip control in the bottom-right.
 *
 * Props:
 *   open        — boolean, mounts the overlay
 *   onDismiss   — called when the sequence completes (naturally or via skip)
 *   fromLocale  — 'en' | 'zh' — for accessibility announcement only
 *   toLocale    — 'en' | 'zh'
 */
export function LanguageTransitionScreen({
  open,
  onDismiss,
  fromLocale,
  toLocale,
}) {
  const { t } = useT("common");
  const reduce = useReducedMotion();
  const rootRef = useRef(null);
  const dismissedRef = useRef(false);

  // Auto-dismiss timer.
  useEffect(() => {
    if (!open) {
      dismissedRef.current = false;
      return undefined;
    }
    // Total target: 2.5s (2100ms hold + 200ms fade-in + 200ms fade-out).
    // Reduced-motion: 1.5s (1100ms hold + 200 + 200).
    const total = reduce ? 1100 : 2100;
    const timer = setTimeout(() => {
      if (!dismissedRef.current) {
        dismissedRef.current = true;
        onDismiss?.();
      }
    }, total);
    return () => clearTimeout(timer);
  }, [open, onDismiss, reduce]);

  // Keyboard handlers (Esc / Enter / Space).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (
        e.key === "Escape" ||
        e.key === "Enter" ||
        e.key === " " ||
        e.key === "Spacebar"
      ) {
        e.preventDefault();
        if (!dismissedRef.current) {
          dismissedRef.current = true;
          onDismiss?.();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onDismiss]);

  // Focus trap: move focus to the overlay so Escape works immediately.
  useEffect(() => {
    if (open && rootRef.current) {
      rootRef.current.focus();
    }
  }, [open]);

  const handleClick = (e) => {
    // Skip button has its own handler that stops propagation.
    if (dismissedRef.current) return;
    dismissedRef.current = true;
    onDismiss?.();
  };

  const handleSkip = (e) => {
    e.stopPropagation();
    if (dismissedRef.current) return;
    dismissedRef.current = true;
    onDismiss?.();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={rootRef}
          role="dialog"
          aria-modal="true"
          aria-live="polite"
          aria-label={`Switching language from ${fromLocale} to ${toLocale}`}
          tabIndex={-1}
          onClick={handleClick}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduce ? 0.1 : 0.2, ease: [0.2, 0.8, 0.2, 1] }}
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden bg-surface-void outline-none cursor-pointer"
          data-testid="language-transition-screen"
        >
          {/* Hex-grid backdrop */}
          <HexGridBackdrop />

          {/* Scan line */}
          {!reduce && <ScanLine />}

          {/* Central mark: icosahedron */}
          <div className="relative z-10 flex flex-col items-center px-6 text-center">
            <IcosahedronMark reduce={reduce} />

            {/* Quote block */}
            <div className="mt-8 max-w-2xl">
              <QuoteLine
                delay={reduce ? 0 : 1.05}
                className="text-2xl leading-snug text-ink-primary md:text-3xl"
                style={{ fontWeight: 500 }}
                data-testid="lts-quote-zh"
              >
                {t("transition.quoteZh")}
              </QuoteLine>
              <QuoteLine
                delay={reduce ? 0.15 : 1.25}
                className="mt-4 text-lg italic leading-snug text-ink-secondary md:text-xl"
                style={{ fontWeight: 300, letterSpacing: "-0.02em" }}
                data-testid="lts-quote-en"
              >
                {t("transition.quoteEn")}
              </QuoteLine>
              <QuoteLine
                delay={reduce ? 0.3 : 1.45}
                className="mt-6 text-sm font-semibold text-ink-tertiary"
                data-testid="lts-attribution"
              >
                {t("transition.attribution")}
              </QuoteLine>
            </div>
          </div>

          {/* Skip control */}
          <button
            type="button"
            onClick={handleSkip}
            className="fixed bottom-8 right-8 z-[101] inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-ink-tertiary transition-colors duration-fast ease-out hover:text-ink-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-frac-violet"
            data-testid="lts-skip"
            aria-label={t("transition.skip")}
          >
            {t("transition.skip")} <span aria-hidden>→</span>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* -------------------------------------------------------------------------- */
/* Sub-visuals                                                                */
/* -------------------------------------------------------------------------- */

function HexGridBackdrop() {
  // Static repeating hex-grid pattern. Very low opacity — feels like stage.
  const hex =
    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='60' height='52' viewBox='0 0 60 52'><path d='M30 1L58 17V35L30 51L2 35V17L30 1z' fill='none' stroke='%237200DB' stroke-width='1' stroke-opacity='0.9'/></svg>\")";
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0"
      style={{
        backgroundImage: hex,
        backgroundRepeat: "repeat",
        backgroundSize: "60px 52px",
        opacity: 0.08,
      }}
    />
  );
}

function ScanLine() {
  return (
    <motion.div
      aria-hidden
      className="pointer-events-none absolute inset-x-0"
      initial={{ y: "40vh", opacity: 0 }}
      animate={{ y: ["40vh", "60vh"], opacity: [0, 0.5, 0] }}
      transition={{ duration: 1.8, ease: "easeInOut", times: [0, 0.5, 1] }}
      style={{
        height: 1,
        background:
          "linear-gradient(90deg, transparent 0%, rgba(114,0,219,0.65) 50%, transparent 100%)",
      }}
    />
  );
}

/**
 * Wireframe icosahedron rendered as a projected 2D SVG. The particles fade
 * in and converge on the vertices; the outline then draws in over 400ms;
 * the assembled mark rotates slowly on its own axis.
 */
function IcosahedronMark({ reduce }) {
  // Precomputed 2D projection of a low-poly icosahedron (fits in a 200×200 box).
  // A simplified set: outer decagon of 10 points plus 2 poles = 12 vertices.
  const R = 42;
  const cx = 0;
  const cy = 0;
  const vertices = [];
  for (let i = 0; i < 10; i++) {
    const angle = (i / 10) * Math.PI * 2 - Math.PI / 2;
    const r = i % 2 === 0 ? R : R * 0.65;
    vertices.push({
      x: cx + Math.cos(angle) * r,
      y: cy + Math.sin(angle) * r,
    });
  }
  vertices.push({ x: cx, y: cy - R * 0.95 });
  vertices.push({ x: cx, y: cy + R * 0.95 });

  // Edges (index pairs). A pleasant subset that visually resembles a low-poly icosahedron.
  const edges = [
    [0, 1], [1, 2], [2, 3], [3, 4], [4, 5],
    [5, 6], [6, 7], [7, 8], [8, 9], [9, 0],
    [0, 10], [2, 10], [4, 10], [6, 10], [8, 10],
    [1, 11], [3, 11], [5, 11], [7, 11], [9, 11],
    [0, 2], [2, 4], [4, 6], [6, 8], [8, 0],
  ];

  const spin = {
    initial: reduce ? { rotate: 0 } : { rotate: -8 },
    animate: reduce ? { rotate: 0 } : { rotate: 8 },
    transition: reduce
      ? { duration: 0 }
      : { duration: 10, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" },
  };

  return (
    <motion.div
      className="relative"
      style={{ width: 120, height: 120 }}
      {...spin}
      data-testid="lts-mark"
    >
      <svg
        viewBox="-70 -70 140 140"
        width="120"
        height="120"
        fill="none"
        stroke="var(--frac-violet)"
        strokeWidth="1.25"
      >
        {/* Fill glow */}
        <defs>
          <radialGradient id="frac-icosa-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#7200DB" stopOpacity="0.32" />
            <stop offset="100%" stopColor="#7200DB" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="0" cy="0" r="56" fill="url(#frac-icosa-glow)" stroke="none" />

        {/* Particles converging into vertices */}
        {!reduce &&
          vertices.map((v, i) => (
            <motion.circle
              key={`p-${i}`}
              cx={v.x}
              cy={v.y}
              r="1.6"
              fill="#7200DB"
              stroke="none"
              initial={{
                cx: (Math.random() - 0.5) * 120,
                cy: (Math.random() - 0.5) * 120,
                opacity: 0,
              }}
              animate={{ cx: v.x, cy: v.y, opacity: 1 }}
              transition={{
                duration: 1.0,
                delay: 0.1 + i * 0.025,
                ease: [0.2, 0.8, 0.2, 1],
              }}
            />
          ))}

        {/* Edges drawing in */}
        {edges.map(([a, b], i) => {
          const va = vertices[a];
          const vb = vertices[b];
          return (
            <motion.line
              key={`e-${i}`}
              x1={va.x}
              y1={va.y}
              x2={vb.x}
              y2={vb.y}
              initial={reduce ? { pathLength: 1, opacity: 0.8 } : { pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 0.9 }}
              transition={{
                duration: reduce ? 0 : 0.5,
                delay: reduce ? 0 : 1.15 + i * 0.008,
                ease: [0.2, 0.8, 0.2, 1],
              }}
            />
          );
        })}

        {/* Vertex dots (final) */}
        {vertices.map((v, i) => (
          <motion.circle
            key={`v-${i}`}
            cx={v.x}
            cy={v.y}
            r="1.8"
            fill="#EDF1F5"
            stroke="none"
            initial={reduce ? { opacity: 0.9 } : { opacity: 0 }}
            animate={{ opacity: 0.95 }}
            transition={{
              duration: reduce ? 0 : 0.25,
              delay: reduce ? 0 : 1.35 + i * 0.01,
            }}
          />
        ))}
      </svg>
    </motion.div>
  );
}

function QuoteLine({ children, delay = 0, className, style, ...rest }) {
  return (
    <div style={{ overflow: "hidden" }}>
      <motion.p
        initial={{ y: "100%", opacity: 0 }}
        animate={{ y: "0%", opacity: 1 }}
        transition={{
          duration: 0.6,
          delay,
          ease: [0.2, 0.8, 0.2, 1],
        }}
        className={className}
        style={style}
        {...rest}
      >
        {children}
      </motion.p>
    </div>
  );
}

export default LanguageTransitionScreen;
